import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import ReactPlayer from "react-player";
import api from "../hooks/useApi";

const DEMO_VIDEOS = [
  {
    _id: "v1",
    title: "Nandurbar Dream Home Construction Timelapse",
    description:
      "Watch a 2BHK home being built from foundation to final paint in 90 seconds.",
    url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    type: "timelapse",
    thumbnail: "",
  },
  {
    _id: "v2",
    title: "RCC Frame Work Process",
    description:
      "Our expert team demonstrates proper RCC column and beam work.",
    url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    type: "process",
    thumbnail: "",
  },
  {
    _id: "v3",
    title: "Client Home Tour — 3 BHK Nandurbar",
    description:
      "Homeowner Suresh Deshmukh gives a tour of his newly completed dream home.",
    url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    type: "testimonial",
    thumbnail: "",
  },
];

const TYPE_LABELS = {
  project: "Project",
  process: "Process",
  testimonial: "Testimonial",
  timelapse: "Timelapse",
};

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await api.getVideos();

        console.log("VIDEOS API:", res);

        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

        if (data.length > 0) {
          setVideos(data);
        } else {
          console.warn("Empty videos, using demo");
          setVideos(DEMO_VIDEOS);
        }
      } catch (err) {
        console.error("VIDEOS ERROR:", err?.response || err.message);
        setVideos(DEMO_VIDEOS);
      }
    };

    fetchVideos();
  }, []);

  const filtered =
    filter === "All" ? videos : videos.filter((v) => v.type === filter);

  return (
    <>
      <Helmet>
        <title>Videos — NirmalRam Construction</title>
        <meta
          name="description"
          content="Watch construction videos, project timelapses, client testimonials, and process walkthroughs from NirmalRam Construction, Nandurbar."
        />
      </Helmet>

      <section
        style={{
          background: "#0A0A0A",
          padding: "8rem 0 4rem",
          borderBottom: "1px solid rgba(201,168,76,0.12)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="grid-bg" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-tag">Our Videos</span>
            <h1
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "1rem",
              }}
            >
              Watch Us Build
              <br />
              Your Dreams
            </h1>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "520px", lineHeight: 1.8 }}>
              Construction timelapses, process walkthroughs, client
              testimonials, and project showcases — see our work in action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section
        style={{
          padding: "2rem 0",
          borderBottom: "1px solid rgba(201,168,76,0.08)",
          background: "#0A0A0A",
          position: "sticky",
          top: "70px",
          zIndex: 50,
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="container">
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {["All", "timelapse", "process", "project", "testimonial"].map(
              (f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: "0.5rem 1.25rem",
                    borderRadius: "2px",
                    fontSize: "0.78rem",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    fontFamily: "Outfit,sans-serif",
                    background: filter === f ? "#C9A84C" : "transparent",
                    color: filter === f ? "#080808" : "#8A8070",
                    border:
                      filter === f
                        ? "1px solid #C9A84C"
                        : "1px solid rgba(201,168,76,0.2)",
                    fontWeight: filter === f ? 600 : 400,
                  }}
                >
                  {f === "All" ? "All Videos" : TYPE_LABELS[f]}
                </button>
              ),
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))",
              gap: "2rem",
            }}
          >
            {filtered.map((v, i) => (
              <motion.div
                key={v._id}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.12)",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                {/* Video player */}
                <div
                  style={{
                    position: "relative",
                    paddingTop: "56.25%",
                    background: "#000",
                    cursor: "pointer",
                  }}
                  onClick={() => setPlaying(playing === v._id ? null : v._id)}
                >
                  {playing === v._id ? (
                    <div style={{ position: "absolute", inset: 0 }}>
                      <ReactPlayer
                        url={v.url}
                        playing
                        width="100%"
                        height="100%"
                        controls
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg,#1a1208,#080808)",
                      }}
                    >
                      {v.thumbnail && (
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            opacity: 0.6,
                          }}
                        />
                      )}
                      <div
                        style={{
                          position: "absolute",
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          background: "rgba(201,168,76,0.9)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.4rem",
                          transition: "0.2s",
                        }}
                      >
                        ▶
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ padding: "1.25rem" }}>
                  <span
                    className="badge badge-gold"
                    style={{ marginBottom: "0.75rem" }}
                  >
                    {TYPE_LABELS[v.type]}
                  </span>
                  <h3
                    style={{
                      fontSize: "1rem",
                      marginBottom: "0.5rem",
                      color: "#F0EAE0",
                    }}
                  >
                    {v.title}
                  </h3>
                  <p
                    style={{
                      color: "#5A5450",
                      fontSize: "0.85rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {v.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
